import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';
import {
  Droplets as PumpIcon,
  CircleDot as ValveIcon,
  Flame as HeaterIcon,
  Lightbulb as LightIcon,
  Fan as MixerIcon,
  Settings as OtherIcon,
  Bug,
} from 'lucide-react';
import { useActuators, useActiveDevice } from '../../hooks/useActuators';
import { EmergencyStopButton } from '../Controls/EmergencyStopButton';
import { TestModeWizardModal } from '../Controls/TestModeWizardModal';
import { Actuator, ActuatorCategory } from '../../types/actuators';
import { Modal } from '../UI/Modal';
import { DimmerKnob } from '../UI/DimmerKnob/DimmerKnob';

interface ActuatorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onActionClick?: (actionId: string, value?: boolean) => void;
}

// Helper functions (reused from QuickActionsSidebar)
const getCategoryIcon = (category: ActuatorCategory): React.ReactNode => {
  switch (category) {
    case 'pump':
      return <PumpIcon size={20} />;
    case 'valve':
      return <ValveIcon size={20} />;
    case 'heater':
      return <HeaterIcon size={20} />;
    case 'light':
      return <LightIcon size={20} />;
    case 'mixer':
      return <MixerIcon size={20} />;
    default:
      return <OtherIcon size={20} />;
  }
};

const getCategoryTone = (
  category: ActuatorCategory,
): { borderClass: string; iconClass: string } => {
  switch (category) {
    case 'pump':
      return {
        borderClass: 'border-l-sky-500',
        iconClass: 'text-sky-500 dark:text-sky-300',
      };
    case 'valve':
      return {
        borderClass: 'border-l-emerald-500',
        iconClass: 'text-emerald-500 dark:text-emerald-300',
      };
    case 'heater':
      return {
        borderClass: 'border-l-red-500',
        iconClass: 'text-red-500 dark:text-red-300',
      };
    case 'light':
      return {
        borderClass: 'border-l-amber-500',
        iconClass: 'text-amber-500 dark:text-amber-300',
      };
    case 'mixer':
      return {
        borderClass: 'border-l-emerald-500',
        iconClass: 'text-emerald-500 dark:text-emerald-300',
      };
    default:
      return {
        borderClass: 'border-l-gray-400',
        iconClass: 'text-gray-500 dark:text-zinc-400',
      };
  }
};

const formatRunTime = (seconds?: number): string => {
  if (!seconds) return '';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
};

const getDimmerColor = (category: ActuatorCategory): string => {
  if (category === 'light') return '#eab308';
  if (category === 'pump') return '#0ea5e9';
  return '#6b7280';
};

export const ActuatorsModal: React.FC<ActuatorsModalProps> = ({
  isOpen,
  onClose,
  onActionClick,
}) => {
  const { activeDeviceId } = useActiveDevice();
  const { t } = useTranslation('components');
  const {
    actuators,
    favorites,
    activeActuators,
    isLoading,
    toggle,
    setValue,
    runTimed,
    emergencyStop,
    testModeState,
    startTestMode,
    stopTestMode,
    clearTestMode,
    resetEmergency,
    isEmergencyStopped,
  } = useActuators({
    deviceId: activeDeviceId || '',
    autoRefresh: isOpen && !!activeDeviceId, // Only refresh when open
    refreshInterval: 3000,
  });

  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'favorites' | 'test'>('all');
  const [isTestWizardOpen, setIsTestWizardOpen] = useState(false);

  const handleToggle = async (actuatorId: string) => {
    try {
      await toggle(actuatorId);
      const actuator = actuators.find((a) => a.id === actuatorId);
      onActionClick?.(actuatorId, actuator?.state !== 'ON');
    } catch (e) {
      logger.error('Actuator command error', { error: e as Error });
    }
  };

  const renderActuatorItem = (actuator: Actuator) => {
    const tone = getCategoryTone(actuator.category);
    const isOn = actuator.state === 'ON';
    const hasDimmer = actuator.category === 'light' || actuator.category === 'pump';

    const toggleRow = (
      <div
        className="flex items-center justify-between"
        onClick={hasDimmer ? () => !isLoading && !actuator.isLocked && handleToggle(actuator.id) : undefined}
        style={hasDimmer ? { cursor: 'pointer' } : undefined}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 dark:bg-black/20 ${
              isOn ? tone.iconClass : 'text-gray-500 dark:text-zinc-400'
            }`}
          >
            {getCategoryIcon(actuator.category)}
          </div>
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">{actuator.name}</div>
            <div className="text-xs text-gray-500">
              {isOn && actuator.runTimeSeconds
                ? `${formatRunTime(actuator.runTimeSeconds)}`
                : actuator.state}
            </div>
          </div>
        </div>

        <div
          className={`w-12 h-6 rounded-full p-1 transition-colors ${isOn ? 'bg-green-500' : 'bg-gray-200 dark:bg-zinc-700'}`}
        >
          <motion.div
            className="w-4 h-4 rounded-full bg-white shadow-sm"
            animate={{ x: isOn ? 24 : 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          />
        </div>
      </div>
    );

    return (
      <div
        key={actuator.id}
        onClick={!hasDimmer ? () => !isLoading && !actuator.isLocked && handleToggle(actuator.id) : undefined}
        className={`bg-white/50 dark:bg-zinc-800/50 rounded-xl border transition-all ${
          hasDimmer ? 'p-3' : 'p-3 cursor-pointer'
        } ${
          isOn
            ? `border-l-4 ${tone.borderClass}`
            : 'border-transparent hover:border-gray-200 dark:hover:border-zinc-700'
        }`}
      >
        {toggleRow}
        {hasDimmer && (
          <div className="mt-3" onClick={(e) => e.stopPropagation()}>
            <DimmerKnob
              value={actuator.value ?? (isOn ? 100 : 0)}
              onChangeEnd={(val) => setValue(actuator.id, val)}
              onToggle={() => handleToggle(actuator.id)}
              onRunTimed={(val, dur) => runTimed(actuator.id, val, dur)}
              presets={[10, 25, 50, 75, 90, 100]}
              durationPresets={[5, 10, 30, 60, 300]}
              onLabel={actuator.category === 'light' ? t('dimmer.on') : t('dimmer.pumpOn')}
              offLabel={actuator.category === 'light' ? t('dimmer.off') : t('dimmer.pumpOff')}
              disabled={actuator.isLocked || isLoading}
              color={getDimmerColor(actuator.category)}
            />
          </div>
        )}
      </div>
    );
  };

  const filteredActuators = (() => {
    if (activeTab === 'favorites') return favorites;
    if (activeTab === 'active') return activeActuators;
    return actuators; // 'all'
  })();

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={t('dashboard.actuatorsModal.title')} size="xl">
        <Modal.Body className="space-y-6">
                  {/* Emergency Stop Banner */}
                  <EmergencyStopButton
                    onEmergencyStop={async () => {
                      await emergencyStop();
                      onActionClick?.('emergency', true);
                    }}
                    onEmergencyReset={async () => {
                      await resetEmergency();
                      onActionClick?.('emergency_reset', true);
                    }}
                    isEmergencyStopped={isEmergencyStopped}
                    activeCount={activeActuators.length}
                    variant="full"
                  />

                  {/* Tabs */}
                  <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-zinc-800">
                    {(['all', 'active', 'favorites', 'test'] as const).map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                          activeTab === tab
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-zinc-400 dark:hover:text-zinc-200'
                        }`}
                      >
                        {tab === 'all' && t('dashboard.actuatorsModal.tabAll')}
                        {tab === 'active' && t('dashboard.actuatorsModal.tabActive', { count: activeActuators.length })}
                        {tab === 'favorites' && t('dashboard.actuatorsModal.tabFavorites')}
                        {tab === 'test' && t('dashboard.actuatorsModal.tabTestMode')}
                      </button>
                    ))}
                  </div>

                  {/* Content */}
                  <div className="min-h-[300px] max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                    {activeTab === 'test' ? (
                      <div className="flex flex-col items-center justify-center p-8 mt-6 text-center bg-gray-50 dark:bg-zinc-800/50 rounded-xl border border-gray-100 dark:border-zinc-800">
                        <Bug className="w-12 h-12 text-cyan-500 mb-4 opacity-80" />
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{t('dashboard.actuatorsModal.diagnosticMode')}</h4>
                        <p className="text-sm text-gray-500 dark:text-zinc-400 mb-6 max-w-sm">
                          {t('dashboard.actuatorsModal.diagnosticDescription')}
                        </p>
                        <button
                          onClick={() => setIsTestWizardOpen(true)}
                          className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 via-teal-500 to-cyan-500 hover:opacity-95 text-white font-medium rounded-xl shadow-lg shadow-emerald-500/25 transition-all focus:ring-4 focus:ring-cyan-500/30"
                        >
                          {t('dashboard.actuatorsModal.startTestWizard')}
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {filteredActuators.length > 0 ? (
                          filteredActuators.map(renderActuatorItem)
                        ) : (
                          <div className="col-span-full py-12 text-center text-gray-500">
                            {activeTab === 'active'
                              ? t('dashboard.actuatorsModal.noActiveItems')
                              : t('dashboard.actuatorsModal.noItemsToShow')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
        </Modal.Body>
      </Modal>

    {/* Test Mode Wizard Modal */}
    {isOpen && isTestWizardOpen && (
      <TestModeWizardModal
        isOpen={isTestWizardOpen}
        onClose={() => setIsTestWizardOpen(false)}
        actuators={actuators}
        testModeState={testModeState}
        onStart={startTestMode}
        onStop={stopTestMode}
        clearTestMode={clearTestMode}
      />
    )}
    </>
  );
};
